import React, { useState, useEffect, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Button, CircularProgress, Tabs, Tab, Grid, InputAdornment, Chip, useMediaQuery
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import CloseIcon from '@mui/icons-material/Close';

import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import api from '../../services/api';
import { getApiErrorMessage, normalizeTeacher } from '../../services/apiUtils';

function isNomeValido(nome) {
    return /^[A-Za-zÀ-ÿ\s]+$/.test(nome);
}

function mascaraCpfCnpj(value = "") {
    value = value.replace(/\D/g, "");
    return value
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
        .slice(0, 14);
}

function mascaraCEP(value = "") {
    return value.replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");
}

function mascaraCelular(value = "") {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 10) {
        // (99) 9999-9999
        return digits
            .replace(/^(\d{0,2})/, "($1")
            .replace(/^\((\d{2})(\d)/, "($1) $2")
            .replace(/(\d{4})(\d)/, "$1-$2");
    }
    // (99) 99999-9999
    return digits
        .replace(/^(\d{0,2})/, "($1")
        .replace(/^\((\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
}

function onlyLettersSpaces(value = "") {
    return String(value)
        .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, "") // só letras e espaço
        .replace(/\s{2,}/g, " ")              // sem espaços duplos
        .trimStart();                         // evita começar com espaço
}

function toTitleCaseNome(value = "") {
    const cleaned = onlyLettersSpaces(value).trim();
    if (!cleaned) return "";

    return cleaned
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

export default function EditAccountDialog({ open, onClose }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { user, login } = useAuth();
    const { showSnackbar } = useSnackbar();

    // Aba ativa
    const [tab, setTab] = useState(0);

    // Pessoais
    const [nome, set_nome] = useState(user?.name || "");
    const [cpf_cnpj, set_cpf_cnpj] = useState(user?.cpf || "");
    const [celular, set_celular] = useState(user?.phone || "");
    const [senha, set_senha] = useState("");
    const [confirmar_senha, set_confirmar_senha] = useState("");

    const passwordValue = senha.trim();

    const passwordRules = [
        {
            key: "minLength",
            valid: passwordValue.length >= 6,
            message: "mínimo de 6 caracteres",
        },
        {
            key: "hasLetter",
            valid: /[A-Za-zÀ-ÿ]/.test(passwordValue),
            message: "pelo menos uma letra",
        },
        {
            key: "hasNumber",
            valid: /\d/.test(passwordValue),
            message: "pelo menos um número",
        },
    ];

    const pendingPasswordRules = passwordRules.filter((rule) => !rule.valid);

    const isPasswordValid = pendingPasswordRules.length === 0;

    const passwordHelperText =
        senha.length === 0
            ? ""
            : isPasswordValid
                ? "Senha válida."
                : `Ainda falta: ${pendingPasswordRules.map((rule) => rule.message).join(", ")}.`;

    // Endereço
    const [cep, set_cep] = useState(user?.zipCode || "");
    const [endereco, set_endereco] = useState(user?.street || "");
    const [numero, set_numero] = useState(user?.number || "");
    const [complemento, set_complemento] = useState(user?.complement || "");
    const [bairro, set_bairro] = useState(user?.district || "");
    const [cidade, set_cidade] = useState(user?.city || "");
    const [uf, set_uf] = useState(user?.state || "");

    // CEP Feedback
    const [cepStatus, setCepStatus] = useState("idle"); // idle | loading | success | error
    const [cepHelper, setCepHelper] = useState("");
    const debounceRef = useRef(null);
    const lastFetchedCepRef = useRef("");

    const [loading, setLoading] = useState(false);

    // Atualiza os campos sempre que abrir o dialog
    useEffect(() => {
        async function fetchUser() {
            const id = user?.id;
            if (id && open) {
                try {
                    const res = await api.get('/teacher');
                    const normalized = normalizeTeacher(res.data);
                    set_nome(normalized?.name || "");
                    set_cpf_cnpj(normalized?.cpf || "");
                    set_celular(normalized?.phone || "");
                    set_cep(normalized?.zipCode || "");
                    set_endereco(normalized?.street || "");
                    set_numero(normalized?.number || "");
                    set_complemento(normalized?.complement || "");
                    set_bairro(normalized?.district || "");
                    set_cidade(normalized?.city || "");
                    set_uf(normalized?.state || "");
                } catch {
                    set_nome(""); set_cpf_cnpj(""); set_celular("");
                    set_cep(""); set_endereco(""); set_numero(""); set_complemento("");
                    set_bairro(""); set_cidade(""); set_uf("");
                }
            }
            set_senha("");
            set_confirmar_senha("");
            setTab(0);
            setCepStatus("idle");
            setCepHelper("");
        }
        if (open) fetchUser();
    }, [open]);

    // Debounce do CEP para ViaCEP
    useEffect(() => {
        const digits = cep.replace(/\D/g, "");
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (digits.length === 8 && lastFetchedCepRef.current !== digits) {
            debounceRef.current = setTimeout(() => buscaCep(digits), 450);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cep]);

    async function buscaCep(cepDigits) {
        try {
            setCepStatus("loading");
            setCepHelper("Buscando endereço...");
            const res = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
            const data = await res.json();

            if (data?.erro) {
                setCepStatus("error");
                setCepHelper("CEP não encontrado. Preencha os dados manualmente.");
                set_endereco("");
                set_bairro("");
                set_cidade("");
                set_uf("");
                lastFetchedCepRef.current = cepDigits;
                return;
            }

            set_endereco(data.logradouro || "");
            set_bairro(data.bairro || "");
            set_cidade(data.localidade || "");
            set_uf(data.uf || "");

            setCepStatus("success");
            setCepHelper("Endereço preenchido automaticamente.");
            document.getElementById("Numero")?.focus();
            lastFetchedCepRef.current = cepDigits;
        } catch (e) {
            setCepStatus("error");
            setCepHelper("Falha ao buscar CEP. Tente novamente.");
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!nome) return showSnackbar("Preencha o nome.", "warning");
        if (!cpf_cnpj) return showSnackbar("Preencha o CPF.", "warning");
        if (!celular) return showSnackbar("Preencha o celular.", "warning");

        if (!cep) return showSnackbar("Preencha o CEP.", "warning");
        if (!endereco) return showSnackbar("Preencha o endereco.", "warning");
        if (!numero) return showSnackbar("Preencha o numero.", "warning");
        if (!bairro) return showSnackbar("Preencha o bairro.", "warning");
        if (!cidade) return showSnackbar("Preencha o cidade.", "warning");
        if (!uf) return showSnackbar("Preencha o UF.", "warning");

        const nomeOk = isNomeValido(nome) && nome.trim().length >= 3;
        if (!nomeOk) return showSnackbar("Nome inválido. Use apenas letras.", "warning");

        if (cpf_cnpj.length !== 14) {
            return showSnackbar("CPF incompleto. Preencha no formato 000.000.000-00.", "warning");
        }

        if (celular.length !== 15) {
            return showSnackbar("Celular incompleto. Preencha no formato (00) 00000-0000.", "warning");
        }

        if (!isNomeValido(nome)) {
            return showSnackbar("Nome deve conter apenas letras.", "error");
        }
        if (senha && !isPasswordValid) {
            return showSnackbar("A nova senha deve ter no mínimo 6 caracteres, contendo letras e números.", "warning");
        }
        if (senha && confirmar_senha.trim() !== passwordValue) {
            return showSnackbar("Senhas não conferem.", "error");
        }
        // validações leves de endereço (opcional obrigatoriedade)
        if (cep && cep.replace(/\D/g, "").length !== 8) {
            return showSnackbar("CEP incompleto.", "warning");
        }
        if (uf && uf.length !== 2) {
            return showSnackbar("UF deve ter 2 letras.", "warning");
        }

        setLoading(true);

        const id = user?.id;
        if (!id) {
            showSnackbar("Usuário não autenticado.", "error");
            setLoading(false);
            return;
        }

        // Monta payload: só manda senha se foi digitada
        const basePayload = {
            name: nome,
            cpf: cpf_cnpj,
            zipCode: cep,
            street: endereco,
            number: numero,
            complement: complemento,
            phone: celular,
            district: bairro,
            city: cidade,
            state: uf,
        };
        const payload = senha ? { ...basePayload, password: senha } : basePayload;

        try {
            await api.put('/teacher', payload);
            const profile = await api.get('/teacher');
            login(normalizeTeacher(profile.data));
            showSnackbar("Dados atualizados com sucesso!", "success");
            onClose();
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, "Erro ao atualizar os dados."), "error");
        }
        setLoading(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm" // mais espaço para grid em desktop
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    p: 0,
                    borderRadius: isMobile ? 0 : 3
                }
            }}
            fullWidth
        >
            <DialogTitle>Editar Conta
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 10 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="fullWidth"
                sx={{ px: 2, pt: 1, mb: 0.5, "& .MuiTab-root": { textTransform: "none", minHeight: 40 } }}
            >
                <Tab label="Dados pessoais" />
                <Tab label="Endereço" />
            </Tabs>

            <form onSubmit={handleSubmit}>
                <DialogContent>

                    {/* TAB 0 - PESSOAIS */}
                    {tab === 0 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Nome"
                                    fullWidth
                                    required
                                    value={nome}
                                    onChange={(e) => set_nome(onlyLettersSpaces(e.target.value))}
                                    onBlur={() => set_nome((prev) => toTitleCaseNome(prev))}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="CPF"
                                    placeholder="Apenas para controle interno"
                                    fullWidth
                                    required
                                    value={cpf_cnpj}
                                    onChange={e => set_cpf_cnpj(mascaraCpfCnpj(e.target.value))}
                                    inputProps={{ maxLength: 14 }}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Celular"
                                    fullWidth
                                    required
                                    value={celular}
                                    onChange={e => set_celular(mascaraCelular(e.target.value))}
                                    inputProps={{ maxLength: 16 }}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nova Senha"
                                    type="password"
                                    fullWidth
                                    value={senha}
                                    onChange={e => set_senha(e.target.value)}
                                    disabled={loading}
                                    error={senha.length > 0 && !isPasswordValid}
                                    helperText={passwordHelperText}
                                    FormHelperTextProps={{
                                        sx: {
                                            color: senha.length > 0 && isPasswordValid ? "success.main" : undefined,
                                            fontWeight: senha.length > 0 ? 600 : 400,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Confirmar Senha Nova"
                                    type="password"
                                    fullWidth
                                    value={confirmar_senha}
                                    onChange={e => set_confirmar_senha(e.target.value)}
                                    disabled={loading}
                                    error={confirmar_senha.length > 0 && confirmar_senha.trim() !== passwordValue}
                                    helperText={
                                        confirmar_senha.length > 0 && confirmar_senha.trim() !== passwordValue
                                            ? "As senhas não conferem."
                                            : ""
                                    }
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* TAB 1 - ENDEREÇO */}
                    {tab === 1 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="CEP"
                                    fullWidth
                                    required
                                    value={cep}
                                    onChange={e => set_cep(mascaraCEP(e.target.value))}
                                    inputProps={{ maxLength: 9 }}
                                    disabled={loading}
                                    inputMode="numeric"
                                    helperText={cepHelper}
                                    FormHelperTextProps={{
                                        sx: { color: cepStatus === "error" ? "error.main" : "text.secondary" },
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {cepStatus === "loading" ? (
                                                    <CircularProgress size={18} />
                                                ) : cepStatus === "success" ? (
                                                    <Chip size="small" color="success" label="OK" sx={{ fontWeight: 800, cursor: "default" }} />
                                                ) : cepStatus === "error" ? (
                                                    <Chip size="small" color="error" label="!" sx={{ fontWeight: 800, cursor: "default" }} />
                                                ) : null}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="Numero"
                                    label="Número"
                                    fullWidth
                                    required
                                    value={numero}
                                    onChange={e => set_numero(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    inputProps={{ inputMode: "numeric" }}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Endereço"
                                    fullWidth
                                    required
                                    value={endereco}
                                    onChange={e => set_endereco(e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Bairro"
                                    fullWidth
                                    required
                                    value={bairro}
                                    onChange={e => set_bairro(e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Cidade"
                                    fullWidth
                                    required
                                    value={cidade}
                                    onChange={e => set_cidade(e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label="UF"
                                    fullWidth
                                    required
                                    value={uf}
                                    onChange={e => set_uf(e.target.value.toUpperCase().slice(0, 2))}
                                    inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Complemento (opcional)"
                                    fullWidth
                                    value={complemento}
                                    onChange={e => set_complemento(e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>
                        </Grid>
                    )}

                </DialogContent>

                <DialogActions sx={{
                    p: 2,
                    position: isMobile ? "fixed" : "static",
                    bottom: isMobile ? 0 : "auto",
                    left: isMobile ? 0 : "auto",
                    right: isMobile ? 0 : "auto",
                    bgcolor: isMobile ? "background.paper" : "transparent",
                    borderTop: isMobile ? `1px solid ${theme.palette.divider}` : "none",
                    zIndex: 10
                }}>
                    <Button onClick={onClose} disabled={loading}>Fechar</Button>
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        sx={{
                            position: 'relative',
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(160,160,160,0.6)',
                                color: '#fff',
                                cursor: 'not-allowed',
                            }
                        }}
                    >
                        {loading && (
                            <CircularProgress
                                size={20}
                                sx={{ color: "white", position: "absolute", left: 16 }}
                            />
                        )}
                        <span style={{ marginLeft: loading ? 24 : 0 }}>
                            {loading ? "Salvando..." : "Salvar"}
                        </span>
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}